/**
 * Keys written to `window.__env` by `nginx/docker-entrypoint.sh` at container start.
 */
export type RuntimeViteEnvKey =
  | "VITE_AVERER_CONFIG_ID"
  | "VITE_DYNAMIC_ENVIRONMENT_ID"
  | "VITE_BLOCKCHAIN_CHAIN_ID"
  | "VITE_WALLET_PROVIDER"
  | "VITE_WALLET_RUNTIME_URL";

const SENTINEL_PREFIX = "__RUNTIME_";

/** True when the value is non-empty and not a leftover `__RUNTIME_*` sentinel. */
const isUsableEnvValue = (value: string | undefined): value is string => {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 && !trimmed.startsWith(SENTINEL_PREFIX);
};

/**
 * Reads a Vite-prefixed runtime value without baking it into the production bundle.
 *
 * Deployed images write values to `window.__env` at container start (one small file).
 * Local Vite and unit tests still use `import.meta.env` / `vi.stubEnv`.
 */
export const getRuntimeViteEnv = (key: RuntimeViteEnvKey): string => {
  const fromWindow = typeof window !== "undefined" ? window.__env?.[key] : undefined;
  if (isUsableEnvValue(fromWindow)) {
    return fromWindow.trim();
  }

  const fromVite = import.meta.env[key];
  if (isUsableEnvValue(fromVite)) {
    return fromVite.trim();
  }

  return "";
};
